/**
 * tutorMediaAccess — la caméra et le micro à l'entrée de la salle du tuteur.
 *
 * **Au web**, l'ancien `PermissionsManager` (`common/video-call/PermissionsManager.js:51-87`) :
 * `getUserMedia({ video, audio })`, les pistes aussitôt arrêtées (la salle publiera les siennes), et la
 * réponse retenue. La salle ne se montre qu'une fois la réponse reçue (`TutorLiveScreen.js:321`) : le
 * navigateur pose ses deux questions d'un coup, et l'accord du micro sert de geste qui débloque la lecture
 * du son de la salle. Un refus y pose la boîte des permissions (web seulement, `useTutorLiveRoom`).
 *
 * **Au natif, aucune capture : les permissions sont demandées, rien n'est ouvert.** Correctif d'hypothèse, gel
 * non reproduit (voir la PR #2957, ligne « Repro ») : sur une tablette Android, à la première entrée, les
 * questions du système puis le fil JS figé, après deux `NegotiationError` (« Local fingerprint does not
 * match identity »). `registerGlobals()` installe bien `getUserMedia` au natif : l'ancienne capture de test
 * ouvrait une VRAIE caméra et un vrai micro pendant que `LiveKitRoom` publiait les siens, deux captures du
 * même appareil ; et `track.stop()` de `@livekit/react-native-webrtc` ne relâche pas la piste de test
 * (`release()` jamais appelé). Le mécanisme probable : une capture qui fuit, un `stopCapture()` qui tient
 * l'exécuteur WebRTC à fil unique pendant `createOffer`, puis la reconnexion complète qui appelle des
 * méthodes natives synchrones sur ce même exécuteur, fil JS bloqué.
 *
 * Les questions ne sont pas non plus laissées à la publication de LiveKit : sur Android, la boîte du système
 * tombait au milieu de sa négociation, et le micro n'était publié qu'une première entrée sur cinq, l'écran
 * disant « Micro ouvert ». Elles sont donc posées ici, le micro puis la caméra, et la salle ne reçoit son
 * jeton qu'après la réponse (`holdConnection` de `useLiveRoom`). Les traces : `useTutorLiveTraces`.
 */
import { getRecordingPermissionsAsync, requestRecordingPermissionsAsync } from "expo-audio"
import { Camera } from "expo-camera"

import { IS_WEB } from "../../shared/platform"
import { permissionAllowed } from "../room/useMediaPermissions"

type MediaDevicesLike = {
    getUserMedia?: (constraints: { video: boolean; audio: boolean }) => Promise<{
        getTracks: () => { stop: () => void }[]
    }>
}

/** Comment la réponse est obtenue : une capture de test (web), ou les permissions demandées (natif). */
export const MEDIA_ACCESS_HOW: "capture" | "permissions_asked" = IS_WEB ? "capture" : "permissions_asked"

/**
 * `true` : accordé ; `false` : refusé ; `null` : on ne sait pas (un navigateur sans `getUserMedia`). Au natif,
 * n'ouvre aucun appareil.
 */
export function requestMediaAccess(): Promise<boolean | null> {
    if (!IS_WEB) return askMediaPermissions()
    const devices = (globalThis as { navigator?: { mediaDevices?: MediaDevicesLike } }).navigator?.mediaDevices
    if (devices?.getUserMedia == null) return Promise.resolve(null)
    return devices.getUserMedia({ video: true, audio: true }).then(
        (stream) => {
            stream.getTracks().forEach((track) => track.stop())
            return true
        },
        () => false
    )
}

/** Le micro et la caméra ensemble : un refus suffit à refuser, il faut les deux pour accorder. */
export function combinedAccess(microphone: boolean | null, camera: boolean | null): boolean | null {
    if (microphone === false || camera === false) return false
    if (microphone === true && camera === true) return true
    return null
}

/**
 * Les questions du système, l'une après l'autre et attendues : le micro, puis la caméra. Déjà accordés, rien
 * n'est demandé. Rien n'est capturé.
 */
async function askMediaPermissions(): Promise<boolean | null> {
    const [mic, cam] = await Promise.all([getRecordingPermissionsAsync(), Camera.getCameraPermissionsAsync()])
    if (mic.status === "granted" && cam.status === "granted") return true
    const microphone = mic.status === "granted" ? mic : await requestRecordingPermissionsAsync()
    const camera = cam.status === "granted" ? cam : await Camera.requestCameraPermissionsAsync()
    return combinedAccess(permissionAllowed(microphone.status), permissionAllowed(camera.status))
}

/** Le navigateur que nomme la boîte des permissions : `browserOf`, dans les règles de la salle (sans Expo). */
export type { TutorBrowser } from "./tutorRoomRules"
